import React, { useEffect, useState } from "react";
import { SelectBox } from "devextreme-react/select-box";
import { FormField, MasterModal } from "../Shared/master";
import useCrudActions from "../../hooks/useCrudActions";
import { isRequestCancelled } from "../../apis/useAxios";
import { showErrorToast } from "../../helpers/message";
import { CREW_TYPES, listedFor } from "./hotelPriorityRules";
import { addPriorityHotel, getHotelOptions } from "./hotelPriorityService";

const BOTH = "PF";
const CHOICES = [
  ...CREW_TYPES.map((c) => ({ id: c.code, label: c.label, icon: c.icon, hint: `${c.label} list only` })),
  { id: BOTH, label: "Both", icon: "fas fa-layer-group", hint: "Cockpit and cabin crew lists" },
];

/** Adds a hotel of the airport at the end of its cockpit, cabin crew or both lists (lists that already have it are skipped). */
export default function AddPriorityHotel({ airportCode: initialAirport, airports, onSaved, onClose }) {
  const [airportCode, setAirportCode] = useState(initialAirport || "");
  const [hotels, setHotels] = useState([]);
  const [hotelCode, setHotelCode] = useState("");
  const [choice, setChoice] = useState(BOTH);
  const [error, setError] = useState("");
  const { busy, save } = useCrudActions();

  useEffect(() => {
    setHotels([]);
    setHotelCode("");
    if (!airportCode) return undefined;
    let active = true;
    getHotelOptions(airportCode)
      .then((list) => active && setHotels(list))
      .catch((err) => !isRequestCancelled(err) && showErrorToast(err?.message));
    return () => {
      active = false;
    };
  }, [airportCode]);

  const hotel = hotels.find((h) => h.hotelCode === hotelCode);
  const taken = listedFor(hotel);
  // A card is open while none of its lists has the hotel; "Both" needs both lists free.
  const isTaken = (id) => [...id].some((c) => taken.includes(c));
  const options = hotels.map((h) => {
    const inBoth = listedFor(h).length === CREW_TYPES.length;
    return { ...h, disabled: inBoth, displayName: inBoth ? `${h.hotelName} (already in both lists)` : h.hotelName };
  });

  // A new hotel that is already in the chosen list moves the choice to the first open card.
  useEffect(() => {
    if (!isTaken(choice)) return;
    const open = CHOICES.find((c) => !isTaken(c.id));
    if (open) setChoice(open.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelCode, hotels]);

  useEffect(() => setError(""), [airportCode, hotelCode]);

  const handleSubmit = async () => {
    const crewTypes = [...choice].filter((c) => !taken.includes(c));
    const problem = !airportCode
      ? "Select the airport."
      : !hotelCode
        ? "Select the hotel."
        : !crewTypes.length || isTaken(choice)
          ? "The hotel is already in this list. Choose another list."
          : "";
    setError(problem);
    if (problem) return;
    await save(() => addPriorityHotel({ airportCode, hotelCode, crewTypes }), {
      onSuccess: async () => {
        await onSaved();
        onClose();
      },
    });
  };

  return (
    <MasterModal isOpen size="md" title="Add hotel to the priority" busy={busy} saveText="Add" onSubmit={handleSubmit} onClose={onClose}>
      <FormField label="Airport" required>
        <SelectBox
          dataSource={airports}
          displayExpr="airportName"
          valueExpr="airportCode"
          value={airportCode || null}
          searchEnabled
          placeholder="Select the airport"
          inputAttr={{ "aria-label": "Airport" }}
          onValueChanged={(e) => setAirportCode(e.value ?? "")}
        />
      </FormField>
      <FormField label="Hotel" required>
        <SelectBox
          dataSource={options}
          displayExpr="displayName"
          valueExpr="hotelCode"
          value={hotelCode || null}
          searchEnabled
          disabled={!airportCode}
          placeholder={airportCode && !hotels.length ? "No active hotel mapped to this airport" : "Select the hotel"}
          inputAttr={{ "aria-label": "Hotel" }}
          onValueChanged={(e) => setHotelCode(e.value ?? "")}
        />
      </FormField>
      <FormField label="Add to" required>
        <div className="hbp-choice" role="radiogroup" aria-label="Add to">
          {CHOICES.map((c) => {
            const blocked = Boolean(hotel) && isTaken(c.id);
            const selected = choice === c.id && !blocked;
            return (
              <label
                key={c.id}
                className={`hbp-choice__item${selected ? " is-selected" : ""}${blocked ? " is-disabled" : ""}`}
                title={blocked ? `${hotel.hotelName} is already in this list` : undefined}
              >
                <input type="radio" name="hbp-crew" value={c.id} checked={selected} disabled={blocked} onChange={() => setChoice(c.id)} />
                <i className={`${c.icon} hbp-choice__icon`} aria-hidden="true" />
                <span className="hbp-choice__text">
                  <span className="hbp-choice__label">{c.label}</span>
                  {blocked ? <span className="hbp-choice__badge">Already listed</span> : <span className="hbp-choice__hint">{c.hint}</span>}
                </span>
                <i className="fas fa-check-circle hbp-choice__check" aria-hidden="true" />
              </label>
            );
          })}
        </div>
      </FormField>
      {error ? (
        <p className="hbp-form-error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden="true" /> {error}
        </p>
      ) : (
        <p className="mst-drawer-hint mb-0">The hotel goes at the end of the list; move it up afterwards if needed.</p>
      )}
    </MasterModal>
  );
}
